import { useEffect, useMemo, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { api } from '../../lib/api'
import { useAuthStore } from '../../stores/auth'
import { Card } from '../../components/ui/Card'
import { Button } from '../../components/ui/Button'
import { Field } from '../../components/ui/Field'
import { Modal } from '../../components/ui/Modal'
import { Pagination, type PageSize } from '../../components/ui/Pagination'
import { ModalTaskCerere } from '../../components/marketing/ModalTaskCerere'
import { CautaFurnizor, type FurnizorAles } from '../../components/marketing/CautaFurnizor'

type Source = 'marketing' | 'vanzari' | 'eveniment' | 'partener_farma' | 'agent_teren'
type Status =
  | 'idee_noua'
  | 'in_verificare'
  | 'brief_trimis'
  | 'in_design'
  | 'bun_tipar_aprobat'
  | 'cotatie_solicitata'
  | 'comanda_lansata'
  | 'in_productie'
  | 'in_receptie'
  | 'receptionata'
  | 'factura_inregistrata'
  | 'finalizata'
  | 'anulata'

interface PrintRequest {
  id: number
  b4lId: string
  source: Source
  sourceDetails: string | null
  materialType: string
  materialDetails: string | null
  quantity: number
  finalQuantity: number | null
  deadline: string | null
  costCenterId: number | null
  status: Status
  notes: string | null
  createdById: number
  assignedToId: number | null
  briefId: number | null
  createdAt: string
  updatedAt: string
  createdBy: { id: number; name: string }
  assignedTo: { id: number; name: string } | null
  costCenter: { id: number; code: string; name: string; department: string } | null
  brief: { id: number; b4lId: string; status: string } | null
  orders: Array<{ id: number; b4lId: string; status: string; supplierEmail: string }>
  /** Task-urile nascute din cerere, prin `refType`/`refId`. Vezi `ModalTaskCerere`. */
  taskuri?: Array<{
    id: number
    title: string
    status: string
    responsabil: string | null
  }>
}

interface CostCenter {
  id: number
  code: string
  name: string
  department: string
  active: boolean
}

const SOURCE_LABELS: Record<Source, string> = {
  marketing: 'Marketing',
  vanzari: 'Vânzări (idei teren)',
  eveniment: 'Eveniment',
  partener_farma: 'Partener farma',
  agent_teren: 'Agent teren',
}

const SOURCE_COLORS: Record<Source, string> = {
  marketing: 'bg-b4l-blue-light text-b4l-blue-dark',
  vanzari: 'bg-b4l-green-light text-b4l-green-dark',
  eveniment: 'bg-b4l-purple-light text-b4l-purple-dark',
  partener_farma: 'bg-b4l-amber-light text-b4l-amber-dark',
  agent_teren: 'bg-b4l-bg text-b4l-text-muted',
}

const STATUS_LABELS: Record<Status, string> = {
  idee_noua: 'Idee nouă',
  in_verificare: 'În verificare',
  brief_trimis: 'Brief trimis',
  in_design: 'În design',
  bun_tipar_aprobat: 'Bun de tipar aprobat',
  cotatie_solicitata: 'Cotație solicitată',
  comanda_lansata: 'Comandă lansată',
  in_productie: 'În producție',
  in_receptie: 'În recepție',
  receptionata: 'Recepționată',
  factura_inregistrata: 'Factură înregistrată',
  finalizata: 'Finalizată',
  anulata: 'Anulată',
}

const STATUS_COLORS: Record<Status, string> = {
  idee_noua: 'bg-b4l-bg text-b4l-text',
  in_verificare: 'bg-b4l-blue-light text-b4l-blue-dark',
  brief_trimis: 'bg-b4l-blue-light text-b4l-blue-dark',
  in_design: 'bg-b4l-purple-light text-b4l-purple-dark',
  bun_tipar_aprobat: 'bg-b4l-green-light text-b4l-green-dark',
  cotatie_solicitata: 'bg-b4l-amber-light text-b4l-amber-dark',
  comanda_lansata: 'bg-b4l-amber-light text-b4l-amber-dark',
  in_productie: 'bg-b4l-amber-light text-b4l-amber-dark',
  in_receptie: 'bg-b4l-blue-light text-b4l-blue-dark',
  receptionata: 'bg-b4l-green-light text-b4l-green-dark',
  factura_inregistrata: 'bg-b4l-green-light text-b4l-green-dark',
  finalizata: 'bg-b4l-green-light text-b4l-green-dark',
  anulata: 'bg-b4l-red-light text-b4l-red-dark',
}

const fmt = (n: number) => n.toLocaleString('ro-RO')

export function CereriPrintPage() {
  const queryClient = useQueryClient()
  const permissions = useAuthStore((s) => s.permissions)
  const canWrite = permissions.includes('marketing.print:write')

  const [sourceFilter, setSourceFilter] = useState<Source | ''>('')
  const [statusFilter, setStatusFilter] = useState<Status | ''>('')
  const [activeOnly, setActiveOnly] = useState(true)
  const [search, setSearch] = useState('')
  const [searchInput, setSearchInput] = useState('')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState<PageSize>(50)
  const [createOpen, setCreateOpen] = useState(false)
  const [editTarget, setEditTarget] = useState<PrintRequest | null>(null)
  const [taskTarget, setTaskTarget] = useState<PrintRequest | null>(null)
  const [detailId, setDetailId] = useState<number | null>(null)

  useEffect(() => {
    setPage(1)
  }, [sourceFilter, statusFilter, activeOnly, search])

  const queryString = useMemo(() => {
    const p = new URLSearchParams()
    if (sourceFilter) p.set('source', sourceFilter)
    if (statusFilter) p.set('status', statusFilter)
    if (activeOnly) p.set('activeOnly', 'true')
    if (search.trim()) p.set('search', search.trim())
    p.set('limit', String(pageSize))
    p.set('offset', String((page - 1) * pageSize))
    return p.toString()
  }, [sourceFilter, statusFilter, activeOnly, search, page, pageSize])

  const listQuery = useQuery<{
    requests: PrintRequest[]
    total: number
    statusCounts: Record<string, number>
  }>({
    queryKey: ['print-requests', queryString],
    queryFn: async () => (await api.get(`/marketing/print-requests?${queryString}`)).data,
  })
  const requests = listQuery.data?.requests ?? []
  const total = listQuery.data?.total ?? 0
  const statusCounts = listQuery.data?.statusCounts ?? {}

  const costCentersQuery = useQuery<{ costCenters: CostCenter[] }>({
    queryKey: ['cost-centers'],
    queryFn: async () => (await api.get('/settings/cost-centers')).data,
  })
  const costCenters = (costCentersQuery.data?.costCenters ?? []).filter((c) => c.active)

  const createMutation = useMutation({
    mutationFn: async (data: Partial<PrintRequest>) =>
      (await api.post('/marketing/print-requests', data)).data,
    onSuccess: () => {
      toast.success('Cerere creată')
      queryClient.invalidateQueries({ queryKey: ['print-requests'] })
      setCreateOpen(false)
    },
    onError: showError,
  })

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: Partial<PrintRequest> }) =>
      (await api.patch(`/marketing/print-requests/${id}`, data)).data,
    onSuccess: () => {
      toast.success('Salvat')
      queryClient.invalidateQueries({ queryKey: ['print-requests'] })
    },
    onError: showError,
  })

  const statusMutation = useMutation({
    mutationFn: async ({ id, status }: { id: number; status: Status }) =>
      (await api.patch(`/marketing/print-requests/${id}/status`, { status })).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['print-requests'] })
    },
    onError: showError,
  })

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => (await api.delete(`/marketing/print-requests/${id}`)).data,
    onSuccess: () => {
      toast.success('Șters')
      queryClient.invalidateQueries({ queryKey: ['print-requests'] })
    },
    onError: showError,
  })

  const summaryStats: Array<{
    status: Status
    label: string
    accent: 'blue' | 'amber' | 'green' | 'red'
  }> = [
    { status: 'idee_noua', label: 'Idei noi', accent: 'blue' },
    { status: 'in_design', label: 'În design', accent: 'amber' },
    { status: 'cotatie_solicitata', label: 'Cotație', accent: 'amber' },
    { status: 'comanda_lansata', label: 'Comandate', accent: 'amber' },
    { status: 'in_receptie', label: 'În recepție', accent: 'blue' },
    { status: 'receptionata', label: 'Recepționate', accent: 'green' },
  ]

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">Cereri tipografie & design</h1>
        {canWrite && <Button onClick={() => setCreateOpen(true)}>+ Cerere nouă</Button>}
      </div>

      {/* Banner stats */}
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
        {summaryStats.map((s) => (
          <button
            key={s.status}
            onClick={() => setStatusFilter(statusFilter === s.status ? '' : s.status)}
            className={`rounded-lg border-2 px-3 py-2 text-left transition ${
              statusFilter === s.status
                ? 'border-b4l-text bg-b4l-bg-soft'
                : 'border-b4l-border bg-white hover:bg-b4l-bg-soft'
            }`}
          >
            <div className="text-[10px] uppercase tracking-wide text-b4l-text-dim">{s.label}</div>
            <div className="text-2xl font-bold">{statusCounts[s.status] ?? 0}</div>
          </button>
        ))}
      </div>

      <Card>
        <div className="mb-3 flex flex-wrap items-center gap-2 text-sm">
          <input
            type="search"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') setSearch(searchInput)
            }}
            onBlur={() => setSearch(searchInput)}
            placeholder="Caută ID B4L / material / partener..."
            className="w-64 rounded border border-b4l-border px-3 py-1.5"
          />
          <select
            value={sourceFilter}
            onChange={(e) => setSourceFilter(e.target.value as Source | '')}
            className="rounded border border-b4l-border bg-white px-2 py-1.5"
          >
            <option value="">Toate sursele</option>
            {(Object.keys(SOURCE_LABELS) as Source[]).map((s) => (
              <option key={s} value={s}>
                {SOURCE_LABELS[s]}
              </option>
            ))}
          </select>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as Status | '')}
            className="rounded border border-b4l-border bg-white px-2 py-1.5"
          >
            <option value="">Toate stările</option>
            {(Object.keys(STATUS_LABELS) as Status[]).map((s) => (
              <option key={s} value={s}>
                {STATUS_LABELS[s]}
              </option>
            ))}
          </select>
          <label className="flex items-center gap-1 text-xs">
            <input
              type="checkbox"
              checked={activeOnly}
              onChange={(e) => setActiveOnly(e.target.checked)}
            />
            Doar active (exclude finalizate/anulate)
          </label>
          <span className="ml-auto text-xs text-b4l-text-muted">{total} cereri</span>
        </div>

        {listQuery.isLoading ? (
          <p className="py-6 text-center text-sm text-b4l-text-muted">Se încarcă...</p>
        ) : requests.length === 0 ? (
          <p className="py-8 text-center text-sm text-b4l-text-muted">
            Nicio cerere. {canWrite && 'Apasă "+ Cerere nouă".'}
          </p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-b4l-border text-left text-xs uppercase tracking-wide text-b4l-text-muted">
                <th className="py-2 pr-3 font-medium">ID B4L</th>
                <th className="py-2 pr-3 font-medium">Sursa</th>
                <th className="py-2 pr-3 font-medium">Material</th>
                <th className="py-2 pr-3 text-right font-medium">Cant.</th>
                <th className="py-2 pr-3 font-medium">Termen</th>
                <th className="py-2 pr-3 font-medium">Centru cost</th>
                <th className="py-2 pr-3 font-medium">Stare</th>
                <th className="py-2 pr-3 font-medium">Brief</th>
                <th className="py-2 pr-3 font-medium">Acțiuni</th>
              </tr>
            </thead>
            <tbody>
              {requests.map((r) => {
                const overdue =
                  r.deadline &&
                  new Date(r.deadline) < new Date() &&
                  !['receptionata', 'factura_inregistrata', 'finalizata', 'anulata'].includes(
                    r.status,
                  )
                return (
                  <tr
                    key={r.id}
                    className={`border-b border-b4l-border last:border-0 align-top ${
                      overdue ? 'bg-b4l-red-light/10' : ''
                    }`}
                  >
                    <td className="py-2 pr-3 font-mono text-xs">{r.b4lId}</td>
                    <td className="py-2 pr-3">
                      <span
                        className={`rounded px-2 py-0.5 text-[11px] ${SOURCE_COLORS[r.source]}`}
                      >
                        {SOURCE_LABELS[r.source]}
                      </span>
                      {r.sourceDetails && (
                        <div className="mt-0.5 text-[10px] text-b4l-text-dim">
                          {r.sourceDetails}
                        </div>
                      )}
                    </td>
                    <td className="py-2 pr-3">
                      <div className="font-medium">{r.materialType}</div>
                      {r.materialDetails && (
                        <div className="line-clamp-1 text-xs text-b4l-text-muted">
                          {r.materialDetails}
                        </div>
                      )}
                    </td>
                    <td className="py-2 pr-3 text-right tabular-nums">
                      {fmt(r.quantity)}
                      {r.finalQuantity && r.finalQuantity !== r.quantity && (
                        <div className="text-[10px] text-b4l-text-dim">
                          → {fmt(r.finalQuantity)}
                        </div>
                      )}
                    </td>
                    <td
                      className={`py-2 pr-3 text-xs ${overdue ? 'font-semibold text-b4l-red-dark' : ''}`}
                    >
                      {r.deadline ? new Date(r.deadline).toLocaleDateString('en-GB') : '—'}
                    </td>
                    <td className="py-2 pr-3 text-xs">
                      {r.costCenter ? (
                        <span title={r.costCenter.name}>{r.costCenter.code}</span>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="py-2 pr-3">
                      {canWrite ? (
                        <select
                          value={r.status}
                          onChange={(e) =>
                            statusMutation.mutate({ id: r.id, status: e.target.value as Status })
                          }
                          className={`rounded px-2 py-0.5 text-[11px] ${STATUS_COLORS[r.status]}`}
                        >
                          {(Object.keys(STATUS_LABELS) as Status[]).map((s) => (
                            <option key={s} value={s}>
                              {STATUS_LABELS[s]}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <span
                          className={`rounded px-2 py-0.5 text-[11px] ${STATUS_COLORS[r.status]}`}
                        >
                          {STATUS_LABELS[r.status]}
                        </span>
                      )}
                    </td>
                    <td className="py-2 pr-3 text-xs">
                      {r.brief ? (
                        <span className="font-mono">{r.brief.b4lId}</span>
                      ) : (
                        <span className="text-b4l-text-dim">—</span>
                      )}
                    </td>
                    <td className="py-2 pr-3">
                      <div className="flex gap-1">
                        <Button
                          variant="secondary"
                          onClick={() => setDetailId(r.id)}
                          className="!px-2 !py-1 !text-xs"
                        >
                          Detalii
                        </Button>
                        {canWrite && (
                          <Button
                            variant="secondary"
                            onClick={() => setEditTarget(r)}
                            className="!px-2 !py-1 !text-xs"
                          >
                            Editează
                          </Button>
                        )}
                        {/*
                          Cand exista deja un task, butonul devine LINK catre el. Altfel se fac
                          trei task-uri pentru aceeasi cerere: nimeni nu tine minte daca a apasat.
                        */}
                        {canWrite &&
                          ((r.taskuri?.length ?? 0) > 0 ? (
                            <a
                              href={`/task-uri?view=${r.taskuri![0].id}`}
                              className="rounded border border-b4l-border bg-b4l-blue-light/40 px-2 py-1 text-xs text-b4l-blue-dark hover:bg-b4l-blue-light"
                              title={r.taskuri!.map((t) => t.title).join(' · ')}
                            >
                              {r.taskuri!.length === 1
                                ? 'Vezi task-ul'
                                : `${r.taskuri!.length} task-uri`}
                            </a>
                          ) : (
                            <Button
                              variant="secondary"
                              onClick={() => setTaskTarget(r)}
                              className="!px-2 !py-1 !text-xs"
                            >
                              + Task
                            </Button>
                          ))}
                        {canWrite && r.status !== 'finalizata' && (
                          <Button
                            variant="danger"
                            onClick={() => {
                              if (confirm(`Șterge ${r.b4lId}?`)) deleteMutation.mutate(r.id)
                            }}
                            className="!px-2 !py-1 !text-xs"
                          >
                            Șterge
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}

        {total > 0 && (
          <div className="mt-3">
            <Pagination
              page={page}
              pageSize={pageSize}
              total={total}
              onPageChange={setPage}
              onPageSizeChange={(n) => {
                setPageSize(n)
                setPage(1)
              }}
              itemLabel="cereri"
            />
          </div>
        )}
      </Card>

      {createOpen && (
        <RequestFormModal
          title="Cerere nouă"
          submitLabel="Creează"
          costCenters={costCenters}
          onClose={() => setCreateOpen(false)}
          onSubmit={(d) => createMutation.mutate(d)}
          submitting={createMutation.isPending}
        />
      )}

      {taskTarget && (
        <ModalTaskCerere
          cerere={taskTarget}
          onClose={() => setTaskTarget(null)}
          onCreat={() => void queryClient.invalidateQueries({ queryKey: ['print-requests'] })}
        />
      )}

      {editTarget && (
        <RequestFormModal
          title={`Editează ${editTarget.b4lId}`}
          submitLabel="Salvează"
          costCenters={costCenters}
          initial={editTarget}
          onClose={() => setEditTarget(null)}
          onSubmit={(d) =>
            updateMutation.mutate(
              { id: editTarget.id, data: d },
              { onSuccess: () => setEditTarget(null) },
            )
          }
          submitting={updateMutation.isPending}
        />
      )}

      {detailId && (
        <RequestDetailModal
          requestId={detailId}
          canWrite={canWrite}
          onClose={() => setDetailId(null)}
        />
      )}
    </div>
  )
}

function showError(err: unknown) {
  const m =
    (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
      ?.message ?? 'Eroare'
  toast.error(m)
}

interface FormProps {
  title: string
  submitLabel: string
  costCenters: CostCenter[]
  initial?: PrintRequest
  onClose: () => void
  onSubmit: (data: Partial<PrintRequest>) => void
  submitting: boolean
}

function RequestFormModal({
  title,
  submitLabel,
  costCenters,
  initial,
  onClose,
  onSubmit,
  submitting,
}: FormProps) {
  const [source, setSource] = useState<Source>(initial?.source ?? 'marketing')
  const [sourceDetails, setSourceDetails] = useState(initial?.sourceDetails ?? '')
  const [materialType, setMaterialType] = useState(initial?.materialType ?? '')
  const [materialDetails, setMaterialDetails] = useState(initial?.materialDetails ?? '')
  const [quantity, setQuantity] = useState(String(initial?.quantity ?? ''))
  const [deadline, setDeadline] = useState(initial?.deadline ? initial.deadline.slice(0, 10) : '')
  const [costCenterId, setCostCenterId] = useState(
    initial?.costCenterId ? String(initial.costCenterId) : '',
  )
  const [notes, setNotes] = useState(initial?.notes ?? '')

  const submit = () => {
    if (!materialType.trim()) {
      toast.error('Tip material obligatoriu')
      return
    }
    const q = parseInt(quantity, 10)
    if (!Number.isFinite(q) || q <= 0) {
      toast.error('Cantitate invalidă')
      return
    }
    onSubmit({
      source,
      sourceDetails: sourceDetails.trim() || null,
      materialType: materialType.trim(),
      materialDetails: materialDetails.trim() || null,
      quantity: q,
      deadline: deadline ? (new Date(deadline).toISOString() as unknown as string) : null,
      costCenterId: costCenterId ? Number(costCenterId) : null,
      notes: notes.trim() || null,
    })
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={title}
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Anulează
          </Button>
          <Button onClick={submit} loading={submitting}>
            {submitLabel}
          </Button>
        </>
      }
    >
      <div className="space-y-3 text-sm">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="mb-1 block text-xs font-medium text-b4l-text-muted">Sursa</label>
            <select
              value={source}
              onChange={(e) => setSource(e.target.value as Source)}
              className="w-full rounded border border-b4l-border bg-white px-3 py-2 text-sm"
            >
              {(Object.keys(SOURCE_LABELS) as Source[]).map((s) => (
                <option key={s} value={s}>
                  {SOURCE_LABELS[s]}
                </option>
              ))}
            </select>
          </div>
          <Field
            label="Detalii sursă (opțional)"
            value={sourceDetails ?? ''}
            onChange={(e) => setSourceDetails(e.target.value)}
            placeholder="ex: Cosmofarm 2026, Catena RO123..."
          />
        </div>
        <Field
          label="Tip material *"
          value={materialType}
          onChange={(e) => setMaterialType(e.target.value)}
          placeholder="ex: Pungă cadou, Catalog, Banner roll-up"
        />
        <div>
          <label className="mb-1 block text-xs font-medium text-b4l-text-muted">
            Specificații / detalii material
          </label>
          <textarea
            value={materialDetails ?? ''}
            onChange={(e) => setMaterialDetails(e.target.value)}
            rows={3}
            placeholder="ex: 20×24×4 cm burduf, hârtie KA 35, tipar 4 culori 100% fontă"
            className="w-full rounded border border-b4l-border px-3 py-2 text-sm"
          />
        </div>
        <div className="grid grid-cols-3 gap-3">
          <Field
            label="Cantitate *"
            type="number"
            min={1}
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
          />
          <Field
            label="Termen"
            type="date"
            value={deadline}
            onChange={(e) => setDeadline(e.target.value)}
          />
          <div>
            <label className="mb-1 block text-xs font-medium text-b4l-text-muted">
              Centru cost
            </label>
            <select
              value={costCenterId}
              onChange={(e) => setCostCenterId(e.target.value)}
              className="w-full rounded border border-b4l-border bg-white px-3 py-2 text-sm"
            >
              <option value="">— niciun centru —</option>
              {costCenters.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.code} — {c.name}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-b4l-text-muted">Note interne</label>
          <textarea
            value={notes ?? ''}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            className="w-full rounded border border-b4l-border px-3 py-2 text-sm"
          />
        </div>
        {initial && (
          <p className="text-[10px] text-b4l-text-dim">
            Creat de {initial.createdBy?.name ?? '?'} la{' '}
            {new Date(initial.createdAt).toLocaleString('en-GB')}
          </p>
        )}
      </div>
    </Modal>
  )
}

// =============================================================================
// REQUEST DETAIL MODAL — tabs: General | Brief | Cotatii | Comenzi | Receptie
// =============================================================================

interface DesignAgency {
  id: number
  name: string
  email: string | null
  phone: string | null
}

interface DesignBriefDraft {
  id: number
  version: number
  fileUrl: string
  feedbackText: string | null
  status: 'pending' | 'accepted' | 'rejected'
  reviewedAt: string | null
  reviewedBy: { id: number; name: string } | null
  createdAt: string
}

interface DesignBrief {
  id: number
  b4lId: string
  agencyId: number | null
  agency: DesignAgency | null
  title: string
  description: string | null
  dimensions: string | null
  deadline: string | null
  cloudFolderUrl: string | null
  status: 'draft' | 'brief_trimis' | 'draft_primit' | 'aprobat' | 'anulat'
  briefSentAt: string | null
  approvedAt: string | null
  finalFileUrl: string | null
  notes: string | null
  drafts: DesignBriefDraft[]
}

interface RequestDetail extends PrintRequest {
  brief: DesignBrief | null
}

type Tab = 'general' | 'brief' | 'quotes' | 'orders' | 'reception'

function RequestDetailModal({
  requestId,
  canWrite,
  onClose,
}: {
  requestId: number
  canWrite: boolean
  onClose: () => void
}) {
  const queryClient = useQueryClient()
  const [tab, setTab] = useState<Tab>('general')

  const detailQuery = useQuery<{ request: RequestDetail }>({
    queryKey: ['print-request-detail', requestId],
    queryFn: async () => (await api.get(`/marketing/print-requests/${requestId}`)).data,
  })

  const r = detailQuery.data?.request

  return (
    <Modal
      open
      onClose={onClose}
      title={r ? `${r.b4lId} — ${r.materialType}` : 'Detalii cerere'}
      size="xl"
      footer={
        <Button variant="secondary" onClick={onClose}>
          Închide
        </Button>
      }
    >
      {detailQuery.isLoading && <p className="text-sm text-b4l-text-muted">Se încarcă...</p>}
      {r && (
        <div className="space-y-3 text-sm">
          {/* Tab bar */}
          <div className="flex gap-1 border-b border-b4l-border">
            <DetailTab active={tab === 'general'} onClick={() => setTab('general')}>
              General
            </DetailTab>
            <DetailTab active={tab === 'brief'} onClick={() => setTab('brief')}>
              Brief design {r.brief ? '✓' : ''}
            </DetailTab>
            <DetailTab active={tab === 'quotes'} onClick={() => setTab('quotes')}>
              Cotații
            </DetailTab>
            <DetailTab active={tab === 'orders'} onClick={() => setTab('orders')}>
              Comenzi {r.orders.length > 0 ? `(${r.orders.length})` : ''}
            </DetailTab>
            <DetailTab active={tab === 'reception'} onClick={() => setTab('reception')}>
              Recepție
            </DetailTab>
          </div>

          {tab === 'general' && <GeneralTab request={r} />}
          {tab === 'brief' && (
            <BriefTab request={r} canWrite={canWrite} onUpdated={() => detailQuery.refetch()} />
          )}
          {tab === 'quotes' && (
            <QuotesTab
              requestId={r.id}
              canWrite={canWrite}
              onUpdated={() => detailQuery.refetch()}
            />
          )}
          {tab === 'orders' && (
            <OrdersTab
              requestId={r.id}
              orders={r.orders}
              canWrite={canWrite}
              onUpdated={() => detailQuery.refetch()}
            />
          )}
          {tab === 'reception' && (
            <ReceptionTab
              requestId={r.id}
              canWrite={canWrite}
              onUpdated={() => detailQuery.refetch()}
            />
          )}
        </div>
      )}
    </Modal>
  )
  void queryClient // touched in mutations within child tabs
}

function DetailTab({
  active,
  onClick,
  children,
}: {
  active: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      onClick={onClick}
      className={`-mb-px border-b-2 px-3 py-1.5 text-sm transition ${
        active
          ? 'border-b4l-blue-dark text-b4l-blue-dark font-medium'
          : 'border-transparent text-b4l-text-muted hover:text-b4l-text'
      }`}
    >
      {children}
    </button>
  )
}

function GeneralTab({ request: r }: { request: RequestDetail }) {
  return (
    <div className="grid grid-cols-2 gap-3 rounded border border-b4l-border bg-b4l-bg-soft p-3">
      <DetailRow label="ID B4L" value={r.b4lId} />
      <DetailRow label="Sursa" value={SOURCE_LABELS[r.source]} />
      <DetailRow label="Detalii sursă" value={r.sourceDetails ?? '—'} />
      <DetailRow label="Material" value={r.materialType} />
      <DetailRow label="Cantitate" value={fmt(r.quantity)} />
      {r.finalQuantity && r.finalQuantity !== r.quantity && (
        <DetailRow label="Cantitate finală" value={fmt(r.finalQuantity)} />
      )}
      <DetailRow
        label="Termen"
        value={r.deadline ? new Date(r.deadline).toLocaleDateString('en-GB') : '—'}
      />
      <DetailRow
        label="Centru cost"
        value={r.costCenter ? `${r.costCenter.code} — ${r.costCenter.name}` : '—'}
      />
      <DetailRow label="Stare" value={STATUS_LABELS[r.status]} />
      <DetailRow label="Asignat" value={r.assignedTo?.name ?? 'Neasignat'} />
      <DetailRow label="Creat de" value={r.createdBy?.name ?? '?'} />
      <DetailRow label="Creat la" value={new Date(r.createdAt).toLocaleString('en-GB')} />
      {r.materialDetails && (
        <div className="col-span-2 border-t border-b4l-border pt-2">
          <div className="text-[10px] uppercase tracking-wide text-b4l-text-dim">
            Specificații material
          </div>
          <div className="whitespace-pre-wrap text-sm">{r.materialDetails}</div>
        </div>
      )}
      {r.notes && (
        <div className="col-span-2 border-t border-b4l-border pt-2">
          <div className="text-[10px] uppercase tracking-wide text-b4l-text-dim">Note interne</div>
          <div className="whitespace-pre-wrap text-sm">{r.notes}</div>
        </div>
      )}
    </div>
  )
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide text-b4l-text-dim">{label}</div>
      <div className="break-words">{value}</div>
    </div>
  )
}

const BRIEF_STATUS_LABELS: Record<DesignBrief['status'], string> = {
  draft: 'Ciornă',
  brief_trimis: 'Brief trimis',
  draft_primit: 'Draft primit',
  aprobat: 'Aprobat',
  anulat: 'Anulat',
}

const BRIEF_STATUS_COLORS: Record<DesignBrief['status'], string> = {
  draft: 'bg-b4l-bg text-b4l-text-muted',
  brief_trimis: 'bg-b4l-blue-light text-b4l-blue-dark',
  draft_primit: 'bg-b4l-amber-light text-b4l-amber-dark',
  aprobat: 'bg-b4l-green-light text-b4l-green-dark',
  anulat: 'bg-b4l-red-light text-b4l-red-dark',
}

function BriefTab({
  request: r,
  canWrite,
  onUpdated,
}: {
  request: RequestDetail
  canWrite: boolean
  onUpdated: () => void
}) {
  const queryClient = useQueryClient()
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [newDraftUrl, setNewDraftUrl] = useState('')
  const [newDraftFeedback, setNewDraftFeedback] = useState('')

  const agenciesQuery = useQuery<{ agencies: DesignAgency[] }>({
    queryKey: ['design-agencies'],
    queryFn: async () => (await api.get('/marketing/design-agencies')).data,
    enabled: !r.brief && showCreateForm,
  })

  const createBriefMutation = useMutation({
    mutationFn: async (data: {
      title: string
      agencyId?: number
      dimensions?: string
      deadline?: string
      description?: string
    }) =>
      (
        await api.post('/marketing/design-briefs', {
          ...data,
          printRequestId: r.id,
        })
      ).data,
    onSuccess: () => {
      toast.success('Brief creat — folder Nextcloud generat (dacă e configurat)')
      onUpdated()
      setShowCreateForm(false)
    },
    onError: showError,
  })

  const sendBriefMutation = useMutation({
    mutationFn: async () =>
      (await api.post(`/marketing/design-briefs/${r.brief!.id}/send-email`, {})).data,
    onSuccess: () => {
      toast.success('Brief trimis pe email către agenție')
      onUpdated()
    },
    onError: showError,
  })

  const addDraftMutation = useMutation({
    mutationFn: async () =>
      (
        await api.post(`/marketing/design-briefs/${r.brief!.id}/drafts`, {
          fileUrl: newDraftUrl.trim(),
          feedbackText: newDraftFeedback.trim() || null,
        })
      ).data,
    onSuccess: () => {
      toast.success('Draft adăugat')
      setNewDraftUrl('')
      setNewDraftFeedback('')
      onUpdated()
    },
    onError: showError,
  })

  const reviewDraftMutation = useMutation({
    mutationFn: async ({
      draftId,
      status,
      feedbackText,
    }: {
      draftId: number
      status: 'accepted' | 'rejected'
      feedbackText?: string
    }) =>
      (
        await api.patch(`/marketing/design-briefs/${r.brief!.id}/drafts/${draftId}`, {
          status,
          feedbackText: feedbackText ?? null,
        })
      ).data,
    onSuccess: () => {
      toast.success('Draft procesat')
      onUpdated()
      queryClient.invalidateQueries({ queryKey: ['print-requests'] })
    },
    onError: showError,
  })

  // Caz 1: NU exista brief
  if (!r.brief) {
    if (!showCreateForm) {
      return (
        <div className="rounded border border-dashed border-b4l-border p-6 text-center">
          <p className="mb-3 text-sm text-b4l-text-muted">
            Nu există brief de design pentru această cerere.
          </p>
          {canWrite && (
            <Button onClick={() => setShowCreateForm(true)}>+ Creează brief design</Button>
          )}
        </div>
      )
    }

    return (
      <CreateBriefForm
        defaultTitle={`Design ${r.materialType} — ${r.b4lId}`}
        defaultDimensions={r.materialDetails ?? ''}
        defaultDeadline={r.deadline ?? ''}
        agencies={agenciesQuery.data?.agencies ?? []}
        onCancel={() => setShowCreateForm(false)}
        onSubmit={(d) => createBriefMutation.mutate(d)}
        submitting={createBriefMutation.isPending}
      />
    )
  }

  // Caz 2: brief existent
  const b = r.brief
  return (
    <div className="space-y-3">
      <div className="rounded border border-b4l-border bg-b4l-bg-soft p-3">
        <div className="mb-2 flex items-center gap-2">
          <span className="font-mono text-sm font-semibold">{b.b4lId}</span>
          <span className={`rounded px-2 py-0.5 text-xs ${BRIEF_STATUS_COLORS[b.status]}`}>
            {BRIEF_STATUS_LABELS[b.status]}
          </span>
        </div>
        <div className="grid grid-cols-2 gap-2 text-xs">
          <DetailRow label="Titlu" value={b.title} />
          <DetailRow label="Agenție" value={b.agency?.name ?? 'Nealocată'} />
          <DetailRow label="Dimensiuni" value={b.dimensions ?? '—'} />
          <DetailRow
            label="Termen draft"
            value={b.deadline ? new Date(b.deadline).toLocaleDateString('en-GB') : '—'}
          />
          {b.briefSentAt && (
            <DetailRow
              label="Brief trimis la"
              value={new Date(b.briefSentAt).toLocaleString('en-GB')}
            />
          )}
          {b.approvedAt && (
            <DetailRow label="Aprobat la" value={new Date(b.approvedAt).toLocaleString('en-GB')} />
          )}
        </div>
        {b.description && (
          <div className="mt-2 border-t border-b4l-border pt-2">
            <div className="text-[10px] uppercase tracking-wide text-b4l-text-dim">Descriere</div>
            <div className="whitespace-pre-wrap text-xs">{b.description}</div>
          </div>
        )}
        {b.cloudFolderUrl && (
          <div className="mt-2 text-xs">
            <span className="text-b4l-text-dim">Folder Nextcloud:</span>{' '}
            <span className="break-all font-mono">{b.cloudFolderUrl}</span>
          </div>
        )}
        {b.finalFileUrl && (
          <div className="mt-2 text-xs">
            <span className="text-b4l-text-dim">Fișier final (BT):</span>{' '}
            <a
              href={b.finalFileUrl}
              target="_blank"
              rel="noreferrer"
              className="text-b4l-blue-dark hover:underline"
            >
              {b.finalFileUrl}
            </a>
          </div>
        )}
        {canWrite && b.status === 'draft' && b.agencyId && (
          <Button
            onClick={() => sendBriefMutation.mutate()}
            loading={sendBriefMutation.isPending}
            className="mt-3 !text-xs"
          >
            ✉ Trimite brief la agenție
          </Button>
        )}
        {canWrite && b.status === 'draft' && !b.agencyId && (
          <p className="mt-2 text-xs text-b4l-amber-dark">
            ⚠ Editează brief-ul și alege o agenție pentru a putea trimite emailul.
          </p>
        )}
      </div>

      {/* Drafts list */}
      <div>
        <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-b4l-text-dim">
          Drafts primite ({b.drafts.length})
        </h4>
        {b.drafts.length === 0 && (
          <p className="text-xs text-b4l-text-muted">
            Nu există drafts. Adaugă unul când agenția trimite primul fișier.
          </p>
        )}
        <div className="space-y-2">
          {b.drafts.map((d) => (
            <div key={d.id} className="rounded border border-b4l-border bg-white p-2 text-xs">
              <div className="mb-1 flex items-center gap-2">
                <span className="font-semibold">v{d.version}</span>
                <span
                  className={`rounded px-1.5 py-0.5 text-[10px] ${
                    d.status === 'accepted'
                      ? 'bg-b4l-green-light text-b4l-green-dark'
                      : d.status === 'rejected'
                        ? 'bg-b4l-red-light text-b4l-red-dark'
                        : 'bg-b4l-amber-light text-b4l-amber-dark'
                  }`}
                >
                  {d.status === 'pending'
                    ? 'În așteptare'
                    : d.status === 'accepted'
                      ? 'Acceptat'
                      : 'Respins'}
                </span>
                <span className="text-b4l-text-dim">
                  {new Date(d.createdAt).toLocaleDateString('en-GB')}
                </span>
                {d.reviewedBy && <span className="text-b4l-text-dim">· {d.reviewedBy.name}</span>}
              </div>
              <a
                href={d.fileUrl}
                target="_blank"
                rel="noreferrer"
                className="break-all text-b4l-blue-dark hover:underline"
              >
                {d.fileUrl}
              </a>
              {d.feedbackText && (
                <div className="mt-1 whitespace-pre-wrap text-b4l-text-muted">
                  Feedback: {d.feedbackText}
                </div>
              )}
              {canWrite && d.status === 'pending' && b.status !== 'aprobat' && (
                <div className="mt-2 flex gap-1">
                  <Button
                    onClick={() =>
                      reviewDraftMutation.mutate({ draftId: d.id, status: 'accepted' })
                    }
                    className="!px-2 !py-1 !text-xs"
                  >
                    ✓ Acceptă (BT final)
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={() => {
                      const fb = prompt('Feedback / motiv respingere (opțional):') ?? ''
                      reviewDraftMutation.mutate({
                        draftId: d.id,
                        status: 'rejected',
                        feedbackText: fb || undefined,
                      })
                    }}
                    className="!px-2 !py-1 !text-xs"
                  >
                    ✗ Respinge
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Add draft form */}
      {canWrite && b.status !== 'aprobat' && b.status !== 'anulat' && (
        <div className="rounded border border-b4l-border p-3">
          <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-b4l-text-dim">
            Adaugă draft nou
          </h4>
          <div className="space-y-2">
            <Field
              label="URL fișier (din Nextcloud)"
              value={newDraftUrl}
              onChange={(e) => setNewDraftUrl(e.target.value)}
              placeholder={b.cloudFolderUrl ? `${b.cloudFolderUrl}/draft-v1.pdf` : 'https://...'}
            />
            <div>
              <label className="mb-1 block text-xs font-medium text-b4l-text-muted">
                Feedback / observații (opțional)
              </label>
              <textarea
                value={newDraftFeedback}
                onChange={(e) => setNewDraftFeedback(e.target.value)}
                rows={2}
                className="w-full rounded border border-b4l-border px-2 py-1 text-xs"
              />
            </div>
            <Button
              onClick={() => {
                if (!newDraftUrl.trim()) {
                  toast.error('URL obligatoriu')
                  return
                }
                addDraftMutation.mutate()
              }}
              loading={addDraftMutation.isPending}
              disabled={!newDraftUrl.trim()}
              className="!text-xs"
            >
              + Adaugă draft v{(b.drafts[0]?.version ?? 0) + 1}
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}

// =============================================================================
// QUOTES TAB
// =============================================================================

interface PrintSupplier {
  id: number
  name: string
  email: string | null
  phone: string | null
  active: boolean
}

interface PrintQuote {
  id: number
  printOrderId: number
  supplierId: number
  pricePerUnit: string | number | null
  currency: string
  totalAmount: string | number | null
  deliveryDays: number | null
  additionalCosts: string | null
  notes: string | null
  isSelected: boolean
  status: 'solicitata' | 'primita' | 'selectata' | 'respinsa'
  emailSentAt: string | null
  emailReceivedAt: string | null
  createdAt: string
  supplier: PrintSupplier
}

const QUOTE_STATUS_LABELS: Record<PrintQuote['status'], string> = {
  solicitata: 'Solicitată',
  primita: 'Primită',
  selectata: 'Selectată ✓',
  respinsa: 'Respinsă',
}

const QUOTE_STATUS_COLORS: Record<PrintQuote['status'], string> = {
  solicitata: 'bg-b4l-amber-light text-b4l-amber-dark',
  primita: 'bg-b4l-blue-light text-b4l-blue-dark',
  selectata: 'bg-b4l-green-light text-b4l-green-dark',
  respinsa: 'bg-b4l-red-light text-b4l-red-dark',
}

function QuotesTab({
  requestId,
  canWrite,
  onUpdated,
}: {
  requestId: number
  canWrite: boolean
  onUpdated: () => void
}) {
  const queryClient = useQueryClient()
  const [showForm, setShowForm] = useState(false)
  const [editing, setEditing] = useState<PrintQuote | null>(null)

  const quotesQuery = useQuery<{ quotes: PrintQuote[] }>({
    queryKey: ['print-quotes', requestId],
    queryFn: async () => (await api.get(`/marketing/print-requests/${requestId}/quotes`)).data,
  })
  const quotes = quotesQuery.data?.quotes ?? []

  const suppliersQuery = useQuery<{ suppliers: PrintSupplier[] }>({
    queryKey: ['print-suppliers'],
    queryFn: async () => (await api.get('/marketing/print-suppliers')).data,
  })
  const suppliers = (suppliersQuery.data?.suppliers ?? []).filter((s) => s.active)

  const createMutation = useMutation({
    mutationFn: async (data: Partial<PrintQuote> & { supplierId: number }) =>
      (await api.post(`/marketing/print-requests/${requestId}/quotes`, data)).data,
    onSuccess: () => {
      toast.success('Cotație adăugată')
      queryClient.invalidateQueries({ queryKey: ['print-quotes', requestId] })
      setShowForm(false)
      onUpdated()
    },
    onError: showError,
  })

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: Partial<PrintQuote> }) =>
      (await api.patch(`/marketing/print-quotes/${id}`, data)).data,
    onSuccess: () => {
      toast.success('Cotație actualizată')
      queryClient.invalidateQueries({ queryKey: ['print-quotes', requestId] })
      setEditing(null)
    },
    onError: showError,
  })

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => (await api.delete(`/marketing/print-quotes/${id}`)).data,
    onSuccess: () => {
      toast.success('Șters')
      queryClient.invalidateQueries({ queryKey: ['print-quotes', requestId] })
    },
    onError: showError,
  })

  const selectMutation = useMutation({
    mutationFn: async (id: number) => (await api.post(`/marketing/print-quotes/${id}/select`)).data,
    onSuccess: () => {
      toast.success('Cotație selectată')
      queryClient.invalidateQueries({ queryKey: ['print-quotes', requestId] })
      onUpdated()
    },
    onError: showError,
  })

  const unselectMutation = useMutation({
    mutationFn: async (id: number) =>
      (await api.post(`/marketing/print-quotes/${id}/unselect`)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['print-quotes', requestId] })
      onUpdated()
    },
    onError: showError,
  })

  const sendRequestMutation = useMutation({
    mutationFn: async (id: number) =>
      (await api.post(`/marketing/print-quotes/${id}/send-request`, {})).data,
    onSuccess: () => {
      toast.success('Email cerere cotație trimis')
      queryClient.invalidateQueries({ queryKey: ['print-quotes', requestId] })
    },
    onError: showError,
  })

  const formatPrice = (n: string | number | null) => {
    if (n == null) return '—'
    const v = typeof n === 'string' ? parseFloat(n) : n
    return v.toLocaleString('ro-RO', { minimumFractionDigits: 2, maximumFractionDigits: 4 })
  }

  return (
    <div className="space-y-3">
      {/* Comparator side-by-side */}
      {quotes.length > 0 && (
        <div className="overflow-x-auto rounded border border-b4l-border">
          <table className="w-full text-xs">
            <thead className="bg-b4l-bg-soft">
              <tr>
                <th className="py-2 px-2 text-left font-medium text-b4l-text-muted">Furnizor</th>
                <th className="py-2 px-2 text-right font-medium text-b4l-text-muted">Preț/buc</th>
                <th className="py-2 px-2 text-right font-medium text-b4l-text-muted">Total</th>
                <th className="py-2 px-2 text-right font-medium text-b4l-text-muted">
                  Termen (zile)
                </th>
                <th className="py-2 px-2 text-left font-medium text-b4l-text-muted">Stare</th>
                <th className="py-2 px-2 text-left font-medium text-b4l-text-muted">Acțiuni</th>
              </tr>
            </thead>
            <tbody>
              {quotes.map((q) => (
                <tr
                  key={q.id}
                  className={`border-t border-b4l-border ${
                    q.isSelected ? 'bg-b4l-green-light/20' : ''
                  }`}
                >
                  <td className="py-2 px-2 align-top">
                    <div className="font-medium">{q.supplier.name}</div>
                    {q.supplier.email && (
                      <div className="text-[10px] text-b4l-text-dim">{q.supplier.email}</div>
                    )}
                    {q.notes && (
                      <div className="mt-0.5 text-[10px] text-b4l-text-muted">{q.notes}</div>
                    )}
                    {q.additionalCosts && (
                      <div className="mt-0.5 text-[10px] text-b4l-amber-dark">
                        ⚠ {q.additionalCosts}
                      </div>
                    )}
                  </td>
                  <td className="py-2 px-2 text-right tabular-nums">
                    {formatPrice(q.pricePerUnit)} {q.currency}
                  </td>
                  <td className="py-2 px-2 text-right font-semibold tabular-nums">
                    {formatPrice(q.totalAmount)} {q.currency}
                  </td>
                  <td className="py-2 px-2 text-right tabular-nums">{q.deliveryDays ?? '—'}</td>
                  <td className="py-2 px-2">
                    <span
                      className={`rounded px-1.5 py-0.5 text-[10px] ${QUOTE_STATUS_COLORS[q.status]}`}
                    >
                      {QUOTE_STATUS_LABELS[q.status]}
                    </span>
                    {q.emailSentAt && (
                      <div className="mt-0.5 text-[10px] text-b4l-text-dim">
                        Email trimis: {new Date(q.emailSentAt).toLocaleDateString('en-GB')}
                      </div>
                    )}
                  </td>
                  <td className="py-2 px-2">
                    {canWrite && (
                      <div className="flex flex-wrap gap-1">
                        {!q.isSelected && (
                          <Button
                            onClick={() => selectMutation.mutate(q.id)}
                            className="!px-2 !py-0.5 !text-[10px]"
                          >
                            ✓ Selectează
                          </Button>
                        )}
                        {q.isSelected && (
                          <Button
                            variant="secondary"
                            onClick={() => unselectMutation.mutate(q.id)}
                            className="!px-2 !py-0.5 !text-[10px]"
                          >
                            Deselectează
                          </Button>
                        )}
                        {q.supplier.email && !q.emailSentAt && (
                          <Button
                            variant="secondary"
                            onClick={() => sendRequestMutation.mutate(q.id)}
                            className="!px-2 !py-0.5 !text-[10px]"
                          >
                            ✉ Cere cotație
                          </Button>
                        )}
                        <Button
                          variant="secondary"
                          onClick={() => setEditing(q)}
                          className="!px-2 !py-0.5 !text-[10px]"
                        >
                          Editează
                        </Button>
                        {!q.isSelected && (
                          <Button
                            variant="danger"
                            onClick={() => {
                              if (confirm(`Șterge cotația de la ${q.supplier.name}?`))
                                deleteMutation.mutate(q.id)
                            }}
                            className="!px-2 !py-0.5 !text-[10px]"
                          >
                            🗑
                          </Button>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {quotes.length === 0 && !showForm && (
        <div className="rounded border border-dashed border-b4l-border p-6 text-center">
          <p className="mb-3 text-sm text-b4l-text-muted">
            Nu există cotații pentru această cerere.
          </p>
          {canWrite && <Button onClick={() => setShowForm(true)}>+ Adaugă prima cotație</Button>}
        </div>
      )}

      {/* Add form */}
      {canWrite && quotes.length > 0 && !showForm && !editing && (
        <Button variant="secondary" onClick={() => setShowForm(true)} className="!text-xs">
          + Adaugă încă o cotație (compară)
        </Button>
      )}

      {showForm && (
        <QuoteForm
          suppliers={suppliers}
          onProfilNou={() => void suppliersQuery.refetch()}
          onCancel={() => setShowForm(false)}
          onSubmit={(d) => createMutation.mutate(d)}
          submitting={createMutation.isPending}
        />
      )}

      {editing && (
        <QuoteForm
          initial={editing}
          suppliers={suppliers}
          onProfilNou={() => void suppliersQuery.refetch()}
          onCancel={() => setEditing(null)}
          onSubmit={(d) => updateMutation.mutate({ id: editing.id, data: d })}
          submitting={updateMutation.isPending}
        />
      )}
    </div>
  )
}

function QuoteForm({
  initial,
  suppliers,
  onProfilNou,
  onCancel,
  onSubmit,
  submitting,
}: {
  initial?: PrintQuote
  suppliers: PrintSupplier[]
  /** Un profil de tipografie nou (sau legat acum): lista se recitește. */
  onProfilNou: () => void
  onCancel: () => void
  onSubmit: (data: {
    supplierId: number
    pricePerUnit?: number | null
    currency: string
    totalAmount?: number | null
    deliveryDays?: number | null
    additionalCosts?: string | null
    notes?: string | null
    status: PrintQuote['status']
  }) => void
  submitting: boolean
}) {
  const [supplierId, setSupplierId] = useState(
    initial?.supplierId ? String(initial.supplierId) : '',
  )
  /*
    Furnizorul se alege din nomenclator, cu câmpul comun de furnizori (pleacă în EA dacă e nou);
    tipografia rămâne „profilul" lui — emailul la care pleacă cererea de cotație. Profilul se
    găsește singur (legat deja, sau unul vechi cu aceeași denumire); altfel se cere emailul.
  */
  const [furnizor, setFurnizor] = useState<FurnizorAles | null>(null)
  const [faraProfil, setFaraProfil] = useState(false)
  const [emailNou, setEmailNou] = useState('')
  const [cautaProfil, setCautaProfil] = useState(false)
  const profil = suppliers.find((s) => String(s.id) === supplierId)

  async function profilPentru(f: FurnizorAles, email?: string) {
    setCautaProfil(true)
    try {
      const r = await api.post<{ supplier: PrintSupplier | null }>(
        '/marketing/print-suppliers/pentru-furnizor',
        { furnizorId: f.id, ...(email ? { email } : {}) },
      )
      if (r.data.supplier) {
        setSupplierId(String(r.data.supplier.id))
        setFaraProfil(false)
        if (!suppliers.some((s) => s.id === r.data.supplier!.id)) onProfilNou()
      } else {
        setSupplierId('')
        setFaraProfil(true)
      }
    } catch (e) {
      toast.error(
        (e as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
          ?.message ?? 'Profilul tipografiei nu a putut fi găsit.',
      )
    } finally {
      setCautaProfil(false)
    }
  }
  const [pricePerUnit, setPricePerUnit] = useState(
    initial?.pricePerUnit != null ? String(initial.pricePerUnit) : '',
  )
  const [currency, setCurrency] = useState(initial?.currency ?? 'EUR')
  const [totalAmount, setTotalAmount] = useState(
    initial?.totalAmount != null ? String(initial.totalAmount) : '',
  )
  const [deliveryDays, setDeliveryDays] = useState(
    initial?.deliveryDays != null ? String(initial.deliveryDays) : '',
  )
  const [additionalCosts, setAdditionalCosts] = useState(initial?.additionalCosts ?? '')
  const [notes, setNotes] = useState(initial?.notes ?? '')
  const [status, setStatus] = useState<PrintQuote['status']>(initial?.status ?? 'primita')

  const submit = () => {
    if (!supplierId) {
      toast.error('Alege furnizor')
      return
    }
    onSubmit({
      supplierId: Number(supplierId),
      pricePerUnit: pricePerUnit ? parseFloat(pricePerUnit) : null,
      currency,
      totalAmount: totalAmount ? parseFloat(totalAmount) : null,
      deliveryDays: deliveryDays ? parseInt(deliveryDays, 10) : null,
      additionalCosts: additionalCosts.trim() || null,
      notes: notes.trim() || null,
      status,
    })
  }

  return (
    <div className="rounded border border-b4l-border p-3">
      <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-b4l-text-dim">
        {initial ? 'Editează cotație' : 'Cotație nouă'}
      </h4>
      <div className="space-y-2 text-sm">
        <div>
          <label className="mb-1 block text-xs font-medium text-b4l-text-muted">Furnizor *</label>
          {initial ? (
            <div className="rounded border border-b4l-border bg-b4l-bg-soft px-3 py-2 text-sm">
              {profil?.name ?? 'furnizorul cotației'}
              {profil?.email ? ` — ${profil.email}` : ''}
            </div>
          ) : (
            <>
              <CautaFurnizor
                value={furnizor}
                onChange={(f) => {
                  setFurnizor(f)
                  setEmailNou('')
                  if (f) void profilPentru(f)
                  else {
                    setSupplierId('')
                    setFaraProfil(false)
                  }
                }}
              />
              {cautaProfil && (
                <p className="mt-1 text-xs text-b4l-text-muted">Se caută profilul tipografiei…</p>
              )}
              {profil && furnizor && (
                <p className="mt-1 text-xs text-b4l-text-muted">
                  Cererea de cotație pleacă la{' '}
                  <span className="font-medium">{profil.email ?? 'niciun email'}</span>.
                </p>
              )}
              {faraProfil && furnizor && (
                <div className="mt-2 flex items-end gap-2">
                  <Field
                    label="Email pentru cererea de cotație"
                    type="email"
                    value={emailNou}
                    onChange={(e) => setEmailNou(e.target.value)}
                    placeholder="ex. office@tipografie.ro"
                    className="flex-1"
                  />
                  <Button
                    variant="secondary"
                    disabled={!emailNou.includes('@') || cautaProfil}
                    onClick={() => void profilPentru(furnizor, emailNou.trim())}
                  >
                    Salvează
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
        <div className="grid grid-cols-3 gap-2">
          <Field
            label="Preț / buc"
            type="number"
            step="0.0001"
            value={pricePerUnit}
            onChange={(e) => setPricePerUnit(e.target.value)}
          />
          <div>
            <label className="mb-1 block text-xs font-medium text-b4l-text-muted">Monedă</label>
            <select
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
              className="w-full rounded border border-b4l-border bg-white px-3 py-2 text-sm"
            >
              <option value="EUR">EUR</option>
              <option value="RON">RON</option>
              <option value="USD">USD</option>
            </select>
          </div>
          <Field
            label="Termen livrare (zile)"
            type="number"
            min={0}
            value={deliveryDays}
            onChange={(e) => setDeliveryDays(e.target.value)}
          />
        </div>
        <Field
          label="Total (calculat sau ofertat)"
          type="number"
          step="0.01"
          value={totalAmount}
          onChange={(e) => setTotalAmount(e.target.value)}
        />
        <Field
          label="Costuri suplimentare (clișee, plastifiere...)"
          value={additionalCosts}
          onChange={(e) => setAdditionalCosts(e.target.value)}
          placeholder="ex: + 250-300 € clișee neincluse"
        />
        <div>
          <label className="mb-1 block text-xs font-medium text-b4l-text-muted">Note</label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            className="w-full rounded border border-b4l-border px-3 py-2 text-sm"
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-b4l-text-muted">Stare</label>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value as PrintQuote['status'])}
            className="rounded border border-b4l-border bg-white px-2 py-1.5 text-sm"
          >
            <option value="solicitata">Solicitată</option>
            <option value="primita">Primită</option>
            <option value="respinsa">Respinsă</option>
          </select>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={onCancel}>
            Anulează
          </Button>
          <Button onClick={submit} loading={submitting}>
            {initial ? 'Salvează' : 'Adaugă cotație'}
          </Button>
        </div>
      </div>
    </div>
  )
}

// =============================================================================
// ORDERS TAB (afisare cotatie selectata + factura SPV matched)
// =============================================================================

interface MatchedAnafInvoice {
  id: number
  docId: string | null
  totalWithTax: string | number | null
  paymentStatus: string
  partenerName: string | null
  cifPartener: string | null
  issueDate: string | null
  currency: string | null
}

type InvoiceCandidate = MatchedAnafInvoice

function OrdersTab({
  requestId,
  orders,
  canWrite,
  onUpdated,
}: {
  requestId: number
  orders: Array<{ id: number; b4lId: string; status: string; supplierEmail: string }>
  canWrite: boolean
  onUpdated: () => void
}) {
  const queryClient = useQueryClient()
  const [showCandidates, setShowCandidates] = useState(false)

  // Re-folosim quotes endpoint ca sa stim cotatia selectata
  const quotesQuery = useQuery<{ quotes: PrintQuote[] }>({
    queryKey: ['print-quotes', requestId],
    queryFn: async () => (await api.get(`/marketing/print-requests/${requestId}/quotes`)).data,
  })
  const selected = (quotesQuery.data?.quotes ?? []).find((q) => q.isSelected)

  const candidatesQuery = useQuery<{
    candidates: InvoiceCandidate[]
    supplier?: string
    expectedTotal?: number
  }>({
    queryKey: ['invoice-candidates', requestId],
    queryFn: async () =>
      (await api.get(`/marketing/print-requests/${requestId}/invoice-candidates`)).data,
    enabled: showCandidates,
  })

  const matchMutation = useMutation({
    mutationFn: async (anafInboundId: number) =>
      (
        await api.post(`/marketing/print-requests/${requestId}/match-invoice`, {
          anafInboundId,
        })
      ).data,
    onSuccess: () => {
      toast.success('Factură asociată')
      queryClient.invalidateQueries({ queryKey: ['print-requests'] })
      queryClient.invalidateQueries({ queryKey: ['print-request-detail', requestId] })
      setShowCandidates(false)
      onUpdated()
    },
    onError: showError,
  })

  const unmatchMutation = useMutation({
    mutationFn: async () =>
      (await api.post(`/marketing/print-requests/${requestId}/unmatch-invoice`)).data,
    onSuccess: () => {
      toast.success('Factură deconectată')
      queryClient.invalidateQueries({ queryKey: ['print-request-detail', requestId] })
      onUpdated()
    },
    onError: showError,
  })

  // Order detail (cu matched invoice) — re-fetch pe detalii cerere ca sa avem matchedAnafInbound
  const orderDetailQuery = useQuery<{
    request: {
      orders: Array<{
        id: number
        b4lId: string
        matchedAnafInbound: MatchedAnafInvoice | null
        selectedQuoteId: number | null
        status: string
      }>
    }
  }>({
    queryKey: ['print-request-detail', requestId],
    queryFn: async () => (await api.get(`/marketing/print-requests/${requestId}`)).data,
  })
  const orderWithFactura = orderDetailQuery.data?.request.orders[0]
  const matchedFactura = orderWithFactura?.matchedAnafInbound ?? null

  if (orders.length === 0) {
    return (
      <p className="text-xs text-b4l-text-muted">
        Nicio comandă încă. Adaugă o cotație în tab-ul Cotații — comanda umbrelă se creează automat.
      </p>
    )
  }

  return (
    <div className="space-y-3 text-sm">
      {/* Cotatie selectata */}
      <div className="rounded border border-b4l-border bg-b4l-bg-soft p-3">
        <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-b4l-text-dim">
          Cotație selectată
        </h4>
        {selected ? (
          <div className="grid grid-cols-2 gap-2 text-xs">
            <DetailRow label="Furnizor" value={selected.supplier.name} />
            <DetailRow label="Email furnizor" value={selected.supplier.email ?? '—'} />
            <DetailRow
              label="Preț / buc"
              value={
                selected.pricePerUnit != null
                  ? `${Number(selected.pricePerUnit).toLocaleString('ro-RO')} ${selected.currency}`
                  : '—'
              }
            />
            <DetailRow
              label="Total"
              value={
                selected.totalAmount != null
                  ? `${Number(selected.totalAmount).toLocaleString('ro-RO')} ${selected.currency}`
                  : '—'
              }
            />
            <DetailRow
              label="Termen livrare"
              value={selected.deliveryDays ? `${selected.deliveryDays} zile` : '—'}
            />
            <DetailRow label="Costuri suplimentare" value={selected.additionalCosts ?? '—'} />
          </div>
        ) : (
          <p className="text-xs text-b4l-text-muted">
            Nicio cotație selectată. Mergi în tab-ul Cotații și marchează una.
          </p>
        )}
      </div>

      {/* Factura SPV */}
      <div className="rounded border border-b4l-border p-3">
        <div className="mb-2 flex items-center justify-between">
          <h4 className="text-xs font-semibold uppercase tracking-wide text-b4l-text-dim">
            Factură SPV
          </h4>
          {matchedFactura && canWrite && (
            <Button
              variant="secondary"
              onClick={() => unmatchMutation.mutate()}
              className="!px-2 !py-0.5 !text-[10px]"
            >
              Deconectează
            </Button>
          )}
        </div>
        {matchedFactura ? (
          <div className="grid grid-cols-2 gap-2 text-xs">
            <DetailRow label="Nr factură" value={matchedFactura.docId ?? '—'} />
            <DetailRow
              label="Data emiterii"
              value={
                matchedFactura.issueDate
                  ? new Date(matchedFactura.issueDate).toLocaleDateString('en-GB')
                  : '—'
              }
            />
            <DetailRow label="Furnizor" value={matchedFactura.partenerName ?? '—'} />
            <DetailRow label="CIF" value={matchedFactura.cifPartener ?? '—'} />
            <DetailRow
              label="Total cu TVA"
              value={
                matchedFactura.totalWithTax != null
                  ? `${Number(matchedFactura.totalWithTax).toLocaleString('ro-RO')} ${matchedFactura.currency ?? 'RON'}`
                  : '—'
              }
            />
            <DetailRow label="Stare plată" value={matchedFactura.paymentStatus} />
          </div>
        ) : (
          <div>
            <p className="mb-2 text-xs text-b4l-text-muted">
              Nicio factură SPV asociată. După ce factura furnizorului ajunge în SPV, o poți asocia
              manual.
            </p>
            {canWrite && selected && (
              <Button
                variant="secondary"
                onClick={() => setShowCandidates(true)}
                className="!text-xs"
              >
                Caută candidați factură SPV
              </Button>
            )}
            {!selected && (
              <p className="text-xs text-b4l-amber-dark">
                ⚠ Selectează mai întâi o cotație ca să poți căuta factura.
              </p>
            )}
          </div>
        )}
      </div>

      {/* Lista candidați */}
      {showCandidates && (
        <div className="rounded border border-b4l-border p-3">
          <div className="mb-2 flex items-center justify-between">
            <h4 className="text-xs font-semibold uppercase tracking-wide text-b4l-text-dim">
              Candidați factură SPV
            </h4>
            <button
              onClick={() => setShowCandidates(false)}
              className="text-xs text-b4l-text-dim hover:text-b4l-text"
            >
              ✕
            </button>
          </div>
          {candidatesQuery.isLoading && <p className="text-xs">Se caută...</p>}
          {candidatesQuery.data && candidatesQuery.data.candidates.length === 0 && (
            <p className="text-xs text-b4l-text-muted">
              Niciun candidat găsit. Pentru {candidatesQuery.data.supplier} cu total apropiat de{' '}
              {candidatesQuery.data.expectedTotal?.toLocaleString('ro-RO')} {selected?.currency}.
            </p>
          )}
          <div className="space-y-1.5">
            {(candidatesQuery.data?.candidates ?? []).map((c) => (
              <div
                key={c.id}
                className="flex items-center justify-between rounded border border-b4l-border bg-white p-2 text-xs"
              >
                <div>
                  <div className="font-mono font-semibold">{c.docId}</div>
                  <div className="text-b4l-text-muted">
                    {c.partenerName} ({c.cifPartener ?? '?'}) ·{' '}
                    {c.issueDate ? new Date(c.issueDate).toLocaleDateString('en-GB') : '—'}
                  </div>
                  <div>
                    {c.totalWithTax != null ? Number(c.totalWithTax).toLocaleString('ro-RO') : '—'}{' '}
                    {c.currency ?? 'RON'} · {c.paymentStatus}
                  </div>
                </div>
                {canWrite && (
                  <Button
                    onClick={() => matchMutation.mutate(c.id)}
                    className="!px-2 !py-1 !text-xs"
                  >
                    Asociază
                  </Button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

// =============================================================================
// RECEPTION TAB
// =============================================================================

interface PrintReception {
  id: number
  printOrderId: number
  receivedDate: string
  receivedBy: { id: number; name: string }
  quantityOrdered: number
  quantityReceived: number
  qualityStatus: 'ok' | 'partial_ok' | 'rejected'
  avizNumber: string | null
  observations: string | null
  photoUrl: string | null
  createdAt: string
}

const QUALITY_LABELS: Record<PrintReception['qualityStatus'], string> = {
  ok: 'OK',
  partial_ok: 'OK parțial (cu rezerve)',
  rejected: 'Respinsă',
}

const QUALITY_COLORS: Record<PrintReception['qualityStatus'], string> = {
  ok: 'bg-b4l-green-light text-b4l-green-dark',
  partial_ok: 'bg-b4l-amber-light text-b4l-amber-dark',
  rejected: 'bg-b4l-red-light text-b4l-red-dark',
}

function ReceptionTab({
  requestId,
  canWrite,
  onUpdated,
}: {
  requestId: number
  canWrite: boolean
  onUpdated: () => void
}) {
  const queryClient = useQueryClient()
  const [showForm, setShowForm] = useState(false)
  const [editing, setEditing] = useState<PrintReception | null>(null)

  const receptionsQuery = useQuery<{
    receptions: PrintReception[]
    summary: { quantityOrdered: number; quantityReceived: number; remaining: number }
  }>({
    queryKey: ['print-receptions', requestId],
    queryFn: async () => (await api.get(`/marketing/print-requests/${requestId}/receptions`)).data,
  })
  const receptions = receptionsQuery.data?.receptions ?? []
  const summary = receptionsQuery.data?.summary

  const createMutation = useMutation({
    mutationFn: async (data: {
      quantityReceived: number
      qualityStatus: PrintReception['qualityStatus']
      receivedDate?: string
      avizNumber?: string | null
      observations?: string | null
      photoUrl?: string | null
    }) => (await api.post(`/marketing/print-requests/${requestId}/receptions`, data)).data,
    onSuccess: () => {
      toast.success('Recepție înregistrată')
      queryClient.invalidateQueries({ queryKey: ['print-receptions', requestId] })
      setShowForm(false)
      onUpdated()
    },
    onError: showError,
  })

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: Partial<PrintReception> }) =>
      (await api.patch(`/marketing/print-receptions/${id}`, data)).data,
    onSuccess: () => {
      toast.success('Salvat')
      queryClient.invalidateQueries({ queryKey: ['print-receptions', requestId] })
      setEditing(null)
    },
    onError: showError,
  })

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => (await api.delete(`/marketing/print-receptions/${id}`)).data,
    onSuccess: () => {
      toast.success('Șters')
      queryClient.invalidateQueries({ queryKey: ['print-receptions', requestId] })
    },
    onError: showError,
  })

  return (
    <div className="space-y-3">
      {summary && summary.quantityOrdered > 0 && (
        <div className="grid grid-cols-3 gap-2">
          <div className="rounded border border-b4l-border bg-b4l-bg-soft p-2">
            <div className="text-[10px] uppercase tracking-wide text-b4l-text-dim">Comandate</div>
            <div className="text-lg font-semibold tabular-nums">{fmt(summary.quantityOrdered)}</div>
          </div>
          <div className="rounded border border-b4l-green-light bg-b4l-green-light/20 p-2">
            <div className="text-[10px] uppercase tracking-wide text-b4l-green-dark">Primite</div>
            <div className="text-lg font-semibold text-b4l-green-dark tabular-nums">
              {fmt(summary.quantityReceived)}
            </div>
          </div>
          <div
            className={`rounded border p-2 ${
              summary.remaining > 0
                ? 'border-b4l-amber-light bg-b4l-amber-light/20'
                : 'border-b4l-border bg-b4l-bg-soft'
            }`}
          >
            <div className="text-[10px] uppercase tracking-wide text-b4l-text-dim">Rămase</div>
            <div
              className={`text-lg font-semibold tabular-nums ${
                summary.remaining > 0 ? 'text-b4l-amber-dark' : ''
              }`}
            >
              {fmt(Math.max(0, summary.remaining))}
            </div>
          </div>
        </div>
      )}

      {receptions.length === 0 && !showForm && (
        <div className="rounded border border-dashed border-b4l-border p-6 text-center">
          <p className="mb-3 text-sm text-b4l-text-muted">Nu există recepții.</p>
          {canWrite && <Button onClick={() => setShowForm(true)}>+ Înregistrează recepție</Button>}
        </div>
      )}

      {receptions.length > 0 && (
        <div className="space-y-2">
          {receptions.map((rec) => (
            <div key={rec.id} className="rounded border border-b4l-border bg-white p-2 text-xs">
              <div className="mb-1 flex items-center gap-2">
                <span className="font-semibold">
                  {new Date(rec.receivedDate).toLocaleDateString('en-GB')}
                </span>
                <span
                  className={`rounded px-1.5 py-0.5 text-[10px] ${QUALITY_COLORS[rec.qualityStatus]}`}
                >
                  {QUALITY_LABELS[rec.qualityStatus]}
                </span>
                <span className="text-b4l-text-dim">de {rec.receivedBy.name}</span>
                {rec.avizNumber && (
                  <span className="text-b4l-text-dim">Aviz: {rec.avizNumber}</span>
                )}
              </div>
              <div className="grid grid-cols-2 gap-2">
                <DetailRow label="Cantitate primită" value={fmt(rec.quantityReceived)} />
                <DetailRow label="Cantitate comandată" value={fmt(rec.quantityOrdered)} />
              </div>
              {rec.observations && (
                <div className="mt-1 whitespace-pre-wrap text-b4l-text-muted">
                  {rec.observations}
                </div>
              )}
              {rec.photoUrl && (
                <a
                  href={rec.photoUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-1 inline-block text-b4l-blue-dark hover:underline"
                >
                  📸 Vezi foto
                </a>
              )}
              {canWrite && (
                <div className="mt-1 flex gap-1">
                  <Button
                    variant="secondary"
                    onClick={() => setEditing(rec)}
                    className="!px-2 !py-0.5 !text-[10px]"
                  >
                    Editează
                  </Button>
                  <Button
                    variant="danger"
                    onClick={() => {
                      if (confirm('Șterge această recepție?')) deleteMutation.mutate(rec.id)
                    }}
                    className="!px-2 !py-0.5 !text-[10px]"
                  >
                    Șterge
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {canWrite &&
        receptions.length > 0 &&
        !showForm &&
        !editing &&
        summary &&
        summary.remaining > 0 && (
          <Button variant="secondary" onClick={() => setShowForm(true)} className="!text-xs">
            + Înregistrează altă recepție (livrare parțială)
          </Button>
        )}

      {showForm && (
        <ReceptionForm
          defaultQty={summary?.remaining ?? 0}
          onCancel={() => setShowForm(false)}
          onSubmit={(d) => createMutation.mutate(d)}
          submitting={createMutation.isPending}
        />
      )}

      {editing && (
        <ReceptionForm
          initial={editing}
          defaultQty={editing.quantityReceived}
          onCancel={() => setEditing(null)}
          onSubmit={(d) => updateMutation.mutate({ id: editing.id, data: d })}
          submitting={updateMutation.isPending}
        />
      )}
    </div>
  )
}

function ReceptionForm({
  initial,
  defaultQty,
  onCancel,
  onSubmit,
  submitting,
}: {
  initial?: PrintReception
  defaultQty: number
  onCancel: () => void
  onSubmit: (data: {
    quantityReceived: number
    qualityStatus: PrintReception['qualityStatus']
    receivedDate?: string
    avizNumber?: string | null
    observations?: string | null
    photoUrl?: string | null
  }) => void
  submitting: boolean
}) {
  const [receivedDate, setReceivedDate] = useState(
    initial ? initial.receivedDate.slice(0, 10) : new Date().toISOString().slice(0, 10),
  )
  const [quantityReceived, setQuantityReceived] = useState(
    String(initial?.quantityReceived ?? defaultQty),
  )
  const [qualityStatus, setQualityStatus] = useState<PrintReception['qualityStatus']>(
    initial?.qualityStatus ?? 'ok',
  )
  const [avizNumber, setAvizNumber] = useState(initial?.avizNumber ?? '')
  const [observations, setObservations] = useState(initial?.observations ?? '')
  const [photoUrl, setPhotoUrl] = useState(initial?.photoUrl ?? '')

  const submit = () => {
    const q = parseInt(quantityReceived, 10)
    if (!Number.isFinite(q) || q < 0) {
      toast.error('Cantitate invalidă')
      return
    }
    onSubmit({
      receivedDate: new Date(receivedDate).toISOString(),
      quantityReceived: q,
      qualityStatus,
      avizNumber: avizNumber.trim() || null,
      observations: observations.trim() || null,
      photoUrl: photoUrl.trim() || null,
    })
  }

  return (
    <div className="rounded border border-b4l-border p-3">
      <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-b4l-text-dim">
        {initial ? 'Editează recepție' : 'Recepție nouă'}
      </h4>
      <div className="space-y-2 text-sm">
        <div className="grid grid-cols-3 gap-2">
          <Field
            label="Data primirii"
            type="date"
            value={receivedDate}
            onChange={(e) => setReceivedDate(e.target.value)}
          />
          <Field
            label="Cantitate primită *"
            type="number"
            min={0}
            value={quantityReceived}
            onChange={(e) => setQuantityReceived(e.target.value)}
          />
          <div>
            <label className="mb-1 block text-xs font-medium text-b4l-text-muted">
              Stare calitate
            </label>
            <select
              value={qualityStatus}
              onChange={(e) => setQualityStatus(e.target.value as PrintReception['qualityStatus'])}
              className="w-full rounded border border-b4l-border bg-white px-2 py-1.5 text-sm"
            >
              <option value="ok">OK (conform)</option>
              <option value="partial_ok">OK parțial (cu rezerve)</option>
              <option value="rejected">Respinsă (recepție anulată)</option>
            </select>
          </div>
        </div>
        <Field
          label="Număr aviz / NIR"
          value={avizNumber}
          onChange={(e) => setAvizNumber(e.target.value)}
          placeholder="ex: AVZ-12345"
        />
        <div>
          <label className="mb-1 block text-xs font-medium text-b4l-text-muted">Observații</label>
          <textarea
            value={observations}
            onChange={(e) => setObservations(e.target.value)}
            rows={2}
            placeholder="Defecte, neconformități, mențiuni..."
            className="w-full rounded border border-b4l-border px-3 py-2 text-sm"
          />
        </div>
        <Field
          label="URL foto (Nextcloud, opțional)"
          value={photoUrl}
          onChange={(e) => setPhotoUrl(e.target.value)}
          placeholder="https://..."
        />
        <div className="flex gap-2">
          <Button variant="secondary" onClick={onCancel}>
            Anulează
          </Button>
          <Button onClick={submit} loading={submitting}>
            {initial ? 'Salvează' : 'Înregistrează recepție'}
          </Button>
        </div>
      </div>
    </div>
  )
}

function CreateBriefForm({
  defaultTitle,
  defaultDimensions,
  defaultDeadline,
  agencies,
  onCancel,
  onSubmit,
  submitting,
}: {
  defaultTitle: string
  defaultDimensions: string
  defaultDeadline: string
  agencies: DesignAgency[]
  onCancel: () => void
  onSubmit: (data: {
    title: string
    agencyId?: number
    dimensions?: string
    deadline?: string
    description?: string
  }) => void
  submitting: boolean
}) {
  const [title, setTitle] = useState(defaultTitle)
  const [agencyId, setAgencyId] = useState('')
  const [dimensions, setDimensions] = useState(defaultDimensions)
  const [deadline, setDeadline] = useState(defaultDeadline ? defaultDeadline.slice(0, 10) : '')
  const [description, setDescription] = useState('')

  const submit = () => {
    if (!title.trim()) {
      toast.error('Titlu obligatoriu')
      return
    }
    onSubmit({
      title: title.trim(),
      agencyId: agencyId ? Number(agencyId) : undefined,
      dimensions: dimensions.trim() || undefined,
      deadline: deadline ? new Date(deadline).toISOString() : undefined,
      description: description.trim() || undefined,
    })
  }

  return (
    <div className="rounded border border-b4l-border p-3">
      <h4 className="mb-3 text-xs font-semibold uppercase tracking-wide text-b4l-text-dim">
        Brief design nou
      </h4>
      <div className="space-y-3 text-sm">
        <Field label="Titlu *" value={title} onChange={(e) => setTitle(e.target.value)} />
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="mb-1 block text-xs font-medium text-b4l-text-muted">Agenție</label>
            <select
              value={agencyId}
              onChange={(e) => setAgencyId(e.target.value)}
              className="w-full rounded border border-b4l-border bg-white px-3 py-2 text-sm"
            >
              <option value="">— alege agenția —</option>
              {agencies.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name} {a.email ? `(${a.email})` : ''}
                </option>
              ))}
            </select>
            {agencies.length === 0 && (
              <p className="mt-1 text-[10px] text-b4l-amber-dark">
                Nicio agenție configurată încă. Cere administratorului să adauge agențiile de design
                în Setări.
              </p>
            )}
          </div>
          <Field
            label="Termen draft"
            type="date"
            value={deadline}
            onChange={(e) => setDeadline(e.target.value)}
          />
        </div>
        <Field
          label="Dimensiuni"
          value={dimensions}
          onChange={(e) => setDimensions(e.target.value)}
          placeholder="ex: 20×24×4 cm burduf, A4, 1080×1080 px"
        />
        <div>
          <label className="mb-1 block text-xs font-medium text-b4l-text-muted">
            Descriere brief
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={4}
            className="w-full rounded border border-b4l-border px-3 py-2 text-sm"
            placeholder="Detalii pentru agenție: stil, mesaje cheie, exemple..."
          />
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={onCancel}>
            Anulează
          </Button>
          <Button onClick={submit} loading={submitting}>
            Creează brief + folder Nextcloud
          </Button>
        </div>
      </div>
    </div>
  )
}
